import React, { useState } from 'react';
import { Modal, Table, message, Button } from 'antd';
import { TerroristApis } from 'apis/TerroristApis';
import { SalesInfoApis } from 'apis/SalesInfoApis';
import { useUser } from 'contexts/UserProvider';

export const useAmlCheck = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [matchedRecords, setMatchedRecords] = useState<any[]>([]);
  const [customer, setCustomer] = useState<any>(null);
  const [onProceedCallback, setOnProceedCallback] = useState<() => void>(
    () => () => {
      // noop
    },
  );
  const { user } = useUser();

  const getDisplayName = (c: any) => {
    if (!c) return '';
    if (c.storeName && c.storeName.trim() !== '') {
      return c.storeName;
    }
    if (c.Surname && c.Name) {
      return `${c.Surname} ${c.Name}`.trim();
    }
    return `${c.firstName || ''} ${c.name || ''}`.trim();
  };

  const checkAndProceed = async (cust: any, onProceed: () => void) => {
    if (!cust) return;

    // Fetch latest customer info to get terrorismType (in case the lightweight list didn't include it)
    try {
      if (cust.id) {
        const res = await SalesInfoApis.getCustomer(cust.id);
        if (res?.data?.getCustomer?.items?.length > 0) {
          const fullCustomer = res.data.getCustomer.items[0];

          // Check if any relevant name was changed in the form
          const nameChanged =
            (cust.name && cust.name !== fullCustomer.name) ||
            (cust.storeName && cust.storeName !== fullCustomer.storeName) ||
            (cust.firstName && cust.firstName !== fullCustomer.firstName) ||
            (cust.Surname && cust.Surname !== fullCustomer.Surname);

          cust = { ...fullCustomer, ...cust };

          if (nameChanged) {
            cust.terrorismType = null;
          }
        }
      }
    } catch (e) {
      console.error('Error fetching latest customer info for AML check', e);
    }

    setCustomer(cust);
    setOnProceedCallback(() => onProceed);

    // If it's explicitly safe
    if (cust.terrorismType === true) {
      onProceed();
      return;
    }

    if (cust.terrorismType === false) {
      Modal.warning({
        title: (
          <div className="flex justify-between items-center text-red-500 font-semibold">
            Khách hàng trong danh sách chống rửa tiền.
          </div>
        ),
        content: (
          <div className="text-gray-600 mt-2">
            Khách hàng &quot;
            {getDisplayName(cust)}&quot; - &quot;
            {cust.sapCustomerId || cust.id || ''}&quot; đang nằm trong danh sách
            cảnh báo (khủng bố/FATF). Không thể tiếp tục thao tác.
          </div>
        ),
        icon: null,
        okText: 'Đóng',
        okButtonProps: { danger: true, type: 'primary' },
        centered: true,
        closable: true,
      });
      return;
    }

    if (cust.terrorismType === null || cust.terrorismType === undefined) {
      const rawNames = [
        cust.storeName, // Tên doanh nghiệp/cửa hàng
        cust.Surname && cust.Name
          ? `${cust.Surname} ${cust.Name}`.trim()
          : `${cust.firstName || ''} ${cust.name || ''}`.trim(), // Tên cá nhân/Người liên hệ đầy đủ
      ];

      const namesToCheck = Array.from(
        new Set(rawNames.map((n) => (n || '').trim()).filter((n) => n !== '')),
      );

      if (namesToCheck.length === 0) {
        onProceed();
        return;
      }

      setLoading(true);
      try {
        const promises: any[] = [];
        for (const n of namesToCheck) {
          promises.push(
            TerroristApis.getRecords({ page: 1, pageSize: 100, fullName: n }),
          );
          promises.push(
            TerroristApis.getFaft({ page: 1, pageSize: 100, firstName: n }),
          );
          promises.push(
            TerroristApis.getPeps({ page: 1, pageSize: 100, personName: n }),
          );
        }

        const results = await Promise.all(promises);

        let terrorItems: any[] = [];
        let fatfItems: any[] = [];
        let pepsItems: any[] = [];

        for (let i = 0; i < namesToCheck.length; i++) {
          const resTerror = results[i * 3];
          const resFatf = results[i * 3 + 1];
          const resPeps = results[i * 3 + 2];
          terrorItems = terrorItems.concat(
            resTerror?.data?.getCounterTerrorismRecords?.items || [],
          );
          fatfItems = fatfItems.concat(
            resFatf?.data?.getCounterTerrorismFaft?.items || [],
          );
          pepsItems = pepsItems.concat(
            resPeps?.data?.getCounterTerrorismPeps?.items || [],
          );
        }

        // Lọc PEPS chỉ lấy quốc gia là Vietnam / Việt Nam
        const isVietnam = (countryStr: string) => {
          if (!countryStr) return false;
          const c = countryStr.toLowerCase();
          return (
            c.includes('vietnam') ||
            c.includes('viet nam') ||
            c.includes('việt nam') ||
            c.includes('vn')
          );
        };

        const validPepsItems = pepsItems.filter((item: any) =>
          isVietnam(item.country),
        );

        // Remove duplicates by ID just in case
        terrorItems = Array.from(
          new Map(terrorItems.map((item) => [item.id, item])).values(),
        );
        fatfItems = Array.from(
          new Map(fatfItems.map((item) => [item.id, item])).values(),
        );
        const filteredPepsItems = Array.from(
          new Map(validPepsItems.map((item) => [item.id, item])).values(),
        );

        if (
          terrorItems.length === 0 &&
          fatfItems.length === 0 &&
          filteredPepsItems.length === 0
        ) {
          // Update customer status to true (safe)
          if (cust.id) {
            await TerroristApis.updateCustomerTerrorism({
              id: cust.id,
              terrorismType: true,
            });
          }
          onProceed();
        } else {
          // Format matched records
          const merged: any[] = [];
          terrorItems.forEach((r: any) => {
            merged.push({
              key: r.id || Math.random().toString(),
              orgName: r.organizationName || '-',
              fullName: r.fullName || '-',
              nickName: r.nickname || '-',
              bod: r.bod || '-',
              gender: r.gender || '-',
              nationality: r.nationality || '-',
              hometown: r.hometown || '-',
              address: r.address || '-',
            });
          });
          fatfItems.forEach((r: any) => {
            merged.push({
              key: r.id || Math.random().toString(),
              orgName: r.type || 'FATF',
              fullName:
                [r.firstName, r.secondName, r.thirdName]
                  .filter(Boolean)
                  .join(' ') || '-',
              nickName: r.aliasName || '-',
              bod: '-',
              gender: '-',
              nationality: r.nationalities || '-',
              hometown: '-',
              address: r.address || '-',
            });
          });
          filteredPepsItems.forEach((r: any) => {
            merged.push({
              key: r.id || Math.random().toString(),
              orgName: 'PEPS (Chính trị)',
              fullName: r.personName || '-',
              nickName: '-',
              bod: r.eventDate ? `Sự kiện: ${r.eventDate}` : '-',
              gender: '-',
              nationality: r.country || 'Việt Nam',
              hometown: '-',
              address: r.eventDescription || '-',
            });
          });
          setMatchedRecords(merged);
          setCustomer(cust);
          setOnProceedCallback(() => onProceed);
          setIsModalOpen(true);
        }
      } catch (err) {
        console.error(err);
        message.error('Lỗi khi kiểm tra dữ liệu AML');
      } finally {
        setLoading(false);
      }
    } else {
      // terrorismType === true
      onProceed();
    }
  };

  const handleConfirm = async (isSafe: boolean) => {
    try {
      setLoading(true);
      if (customer?.id) {
        await TerroristApis.updateCustomerTerrorism({
          id: customer.id,
          terrorismType: isSafe,
          terrorismUserApproval: user?.id,
          terrorismUserDate: new Date().toISOString(),
          terrorismUserPlant: user?.plantCode || user?.plantId,
        });
      }

      // Update local object so next click sees the updated status
      if (customer) {
        customer.terrorismType = isSafe;
        customer.terrorismUserApproval = user?.id;
        customer.terrorismUserDate = new Date().toISOString();
        customer.terrorismUserPlant = user?.plantCode || user?.plantId;
      }

      setIsModalOpen(false);

      if (isSafe) {
        message.success('Xác nhận thành công!');
        onProceedCallback();
      }
    } catch (err) {
      console.error(err);
      message.error('Lỗi cập nhật trạng thái xác minh');
    } finally {
      setLoading(false);
    }
  };

  const amlModalContext = (
    <Modal
      title="Khách hàng có trong danh sách cần kiểm tra"
      open={isModalOpen}
      closeIcon={<span className="text-gray-400 text-lg">✕</span>}
      onCancel={() => setIsModalOpen(false)}
      width={1000}
      footer={[
        <Button
          key="safe"
          type="primary"
          loading={loading}
          onClick={() => handleConfirm(true)}
          className="bg-[#0b9c2c] hover:!bg-[#098224] border-none"
        >
          An toàn
        </Button>,
        <Button
          key="unsafe"
          type="primary"
          danger
          loading={loading}
          onClick={() => handleConfirm(false)}
          className="bg-[#e03131] hover:!bg-[#c92a2a] border-none"
        >
          Không an toàn
        </Button>,
      ]}
    >
      <div className="mb-4 text-gray-600">
        Khách hàng &quot;
        {getDisplayName(customer)}
        &quot; hiện đang nằm trong danh sách phòng chống khủng bố, danh sách
        FATF hoặc danh sách PEPS.
        <br />
        Danh sách các tên liên quan được hiển thị bên dưới. Trường hợp khách
        hàng không thuộc danh sách cảnh báo, bạn có thể chọn Xác nhận để tiếp
        tục xử lý. Ngược lại, nếu khách hàng thuộc danh sách cảnh báo, vui lòng
        không xác nhận.
        <br />
        Thông tin xác nhận của bạn sẽ được gửi đến quản lý để kiểm tra.
      </div>
      <Table
        dataSource={matchedRecords}
        pagination={false}
        size="small"
        bordered
        scroll={{ y: 300, x: 1000 }}
        columns={[
          { title: 'Tên tổ chức', dataIndex: 'orgName' },
          { title: 'Tên cá nhân', dataIndex: 'fullName' },
          { title: 'Nick Name / Tên khác', dataIndex: 'nickName' },
          { title: 'Ngày Sinh', dataIndex: 'bod' },
          { title: 'Giới tính', dataIndex: 'gender' },
          { title: 'Quốc tịch', dataIndex: 'nationality' },
          { title: 'Quê quán', dataIndex: 'hometown' },
          { title: 'Địa chỉ cư trú', dataIndex: 'address' },
        ]}
      />
    </Modal>
  );

  return {
    checkAndProceed,
    amlModalContext,
    isAmlLoading: loading,
  };
};
